import React, { useState, useRef, useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import fileExtension from 'file-extension';
import { withTranslation } from 'react-i18next';

import { makeGetCurrentUserId } from '../selectors/users';
import { sendMessage } from '../actions/messages';

import config from '../config';
import { trackEvent } from '../services/events-tracker-service';
import { getTypeKey } from '../utils/message';
import { emojisRegex } from '../md/plugins/emoji';

import DiscussionInput from './discussion-input';
import MeetingBanner from './MeetingBanner';
import Zoom from './zoom';
import DiscussionMessages from 'components/DiscussionMessages';

import IconClose from '../icons/icon-close';

const Discussion = ({ systemAsAction, botUseUserName, smallerSystem, canCreateTask, autofocus, compact, compactInput, directUpload, dateFormat, emptyComponent, filePreviewPath, gifSize, headerEnabled, hideSystem, excludeRemoved, id, docId, fileId, inputPlaceholer, localDropzone, noDivider, controlDeleteText, onBlur, onClose, onFocus, onHeaderClick, onNewMessage, onThreadActivate, onPinMessage, projectId, provideController, showSendToggle, startFromMessage, subtitle, title, type, urlPreviewEnabled, users, utils, meeting, pinnedMessage, pinnedMessageBy, emptyText, currentUserId,canHideFromGuests, canSendAsEmail, canLinkMessage, onNotFound, t }) => {

    const [meetingJoined, setMeetingJoined] = useState(false);
    const [focused, setFocused] = useState(false);
    const inputCtrl = useRef(null);
    const messagesCtrl = useRef(null);
    const unmounted = useRef(false);
    const unfocus = useRef(null);

    const shouldScrollBottom = useCallback(() => {
        messagesCtrl.current?.shouldScrollBottom();
    }, [messagesCtrl]);

    const scrollToBottom = useCallback(() => {
        messagesCtrl.current?.scrollToBottom();
    }, [messagesCtrl]);

    const addMessage = useCallback(({ type: messageType = 'text', text, url, files, doc_attachment, hidden, audio_duration }) => {
        const localFiles = (files && files.filter(file => !file.serviceType)) || [];

        scrollToBottom();

        sendMessage({
            file: fileId,
            [getTypeKey(type)]: id,
            text,
            type: messageType,
            url,
            files,
            hidden,
            document: docId,
            doc_attachment,
            audio_duration
        }).then(messageId => {
            if (!doc_attachment) {
                if (text) {
                    trackEvent('MESSAGE.POST', {
                        messageId,
                        type: 'Text message' + (RegExp(emojisRegex, 'g').test(text) ? ' + emoji' : '') + (localFiles.length ? ' + files' : ''),
                        via: type
                    });
                } else {
                    trackEvent('MESSAGE.POST', {
                        messageId,
                        type: 'Files message',
                        via: type
                    });
                }
            }
        });

        scrollToBottom();

        onNewMessage && onNewMessage();
    }, [scrollToBottom, id, type, docId, onNewMessage]);

    const focus = useCallback((force = true) => {
        if (!force && focused) {
            return;
        }

        inputCtrl.current && inputCtrl.current.focus();
    }, [focused, inputCtrl]);

    const handleHeightChange = useCallback(() => {
        if (shouldScrollBottom()) {
            scrollToBottom();
        }
    }, [shouldScrollBottom, scrollToBottom]);

    const handleNewMessage = useCallback((message, files = [], { hideFromGuests } = {}) => {
        addMessage({
            files,
            ...message,
            hidden: hideFromGuests ? ['client'] : undefined
        })

        if (type === 'chat') {
            trackEvent('CHAT.POST');
        } else {
            trackEvent('DISCUSSION.POST');
        }

        files.forEach(file => {
            trackEvent('FILE.UPLOAD', {
                via: 'Discussion',
                id: file.id,
                fileType: fileExtension(file.name),
                fileSize: file.size
            });
        });
    }, [addMessage, type]);

    const handleMeetingJoin = useCallback(() => {
        if (config.ZOOM_USE_SDK && meeting.provider === 'zoom') {
            setMeetingJoined(true);
        } else {
            const win = window.open(meeting.joinUrl, '_blank');
            win && win.focus();
        }
    }, [meeting, setMeetingJoined]);

    const handleMeetingLeave = useCallback(() => {
        if (unmounted.current) {
            return;
        }

        if (!meetingJoined) { 
            return;
        }

        setMeetingJoined(false);
    }, [unmounted.current, meetingJoined, setMeetingJoined]);

    const handleUpPress = useCallback(event => {
        event.preventDefault();

        messagesCtrl.current?.editLastSelfMessage();
    }, [messagesCtrl.current])

    const handleFocus = useCallback(() => {
        setFocused(true);

        if (unfocus.current) {
            clearTimeout(unfocus.current);
            unfocus.current = null;
        }

        onFocus && onFocus();
    }, [setFocused, onFocus]);

    const handleBlur = useCallback(() => {
        setFocused(false);

        onBlur && onBlur();
    }, [setFocused, onBlur]);

    const handleMessagesClick = useCallback(() => { 
        if (!focused) {
            setFocused(true);

            unfocus.current = setTimeout(() => {
                setFocused(false);
            }, 500);
        }
    }, [focused, inputCtrl]);


    useEffect(() => {
        provideController && provideController({
            focus: () => {
                focus();
            },
            addMessage: (text, files) => {
                addMessage({ text, files });
            }
        });

        return () => {
            unmounted.current = true;

            provideController && provideController(null);
        };
    }, []);

    return (
        <div className={'discussion' + (compact ? ' compact' : '') + (noDivider ? ' no-divider' : '') + (systemAsAction ? ' system-as-action' : '') + (smallerSystem ? ' smaller-system' : '')}>

            {
                headerEnabled ? (
                    <div className="discussion-head" onClick={onHeaderClick}>
                        <div className="discussion-name">
                            <h3>
                                <span className="label">
                                    {title}
                                </span>
                            </h3>

                            { 
                                subtitle ? (
                                    <h4>{subtitle}</h4>
                                ) : null
                            }
                        </div>

                        {
                            onClose ? (
                                <button className="discussion-close btn btn-small btn-simple-close" onClick={onClose}>
                                    <IconClose />
                                </button>
                            ) : null
                        }
                    </div>
                ) : null
            }

            <MeetingBanner joinUrl={meeting?.joinUrl} provider={meeting?.provider} onJoin={handleMeetingJoin} />

            <Zoom meetingId={meetingJoined && meeting?.provider === 'zoom' && meeting.id} password={meeting?.password} onLeave={handleMeetingLeave} />
            
            <DiscussionMessages
                id={id}
                type={type}
                projectId={projectId}
                startFromMessage={startFromMessage}
                filePreviewPath={filePreviewPath}
                emptyComponent={emptyComponent}
                emptyText={emptyText}
                canCreateTask={canCreateTask}
                dateFormat={dateFormat}
                gifSize={gifSize}
                users={users}
                urlPreviewEnabled={urlPreviewEnabled}
                systemAsAction={systemAsAction}
                botUseUserName={botUseUserName}
                controlDeleteText={controlDeleteText}
                pinnedMessage={pinnedMessage}
                pinnedMessageBy={pinnedMessageBy}
                noDivider={noDivider}
                hideSystem={hideSystem}
                excludeRemoved={excludeRemoved}
                focused={focused}
                canSendAsEmail={canSendAsEmail}
                canLinkMessage={canLinkMessage}
                onNotFound={onNotFound}
                onClick={handleMessagesClick}
                onThreadActivate={onThreadActivate}
                onPinMessage={onPinMessage}
                provideController={messagesCtrl}
            />

            <div className="discussion-foot">
                <DiscussionInput
                    key={id}
                    type={type}
                    compact={compactInput}
                    directUpload={directUpload}
                    currentUserId={currentUserId}
                    placeholder={inputPlaceholer}
                    projectId={projectId}
                    discussionName={title}
                    autofocus={autofocus}
                    utils={utils}
                    discussionId={id}
                    showSendToggle={showSendToggle}
                    localDropzone={localDropzone}
                    canHideFromGuests={canHideFromGuests}
                    users={users}
                    onNewMessage={handleNewMessage}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onHeightChange={handleHeightChange}
                    onUpPress={handleUpPress}
                    provideController={inputCtrl}
                />
            </div>
        </div>
    );
}

Discussion.defaultProps = {
    headerEnabled: false,
    dateFormat: 'time',
    gifSize: ''
}

const makeMapStateToProps = () => {
    const getCurrentUserId = makeGetCurrentUserId();

    return ({ users }, ownProps) => {
        return {
            currentUserId: getCurrentUserId(users)
        }
    }
}

export default connect(makeMapStateToProps)(withTranslation('chat')(Discussion));
